import { useState } from "react";
import { Plus, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useLookupOptions, addLookupValue } from "../lib/lookups";
import { formatApiError } from "../lib/api";

const NONE = "__none__";
const ADD = "__add__";

/** Select backed by an editable lookup list; "Aggiungi nuovo…" saves the value into the list and selects it. */
export default function LookupSelect({ lookup, value, onChange, testid, placeholder = "Seleziona", required = false, className = "" }) {
  const options = useLookupOptions(lookup);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const known = options.some((o) => o.value === value);

  const save = async () => {
    if (!draft.trim()) return setAdding(false);
    setSaving(true);
    try {
      const v = await addLookupValue(lookup, draft);
      onChange(v);
      toast.success(`"${v}" aggiunto alla lista`);
      setAdding(false); setDraft("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); } finally { setSaving(false); }
  };

  if (adding) {
    return (
      <div className="flex items-center gap-1.5" data-testid={`${testid}-add-row`}>
        <Input autoFocus data-testid={`${testid}-add-input`} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Nuovo valore…"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") setAdding(false); }} className="h-10" />
        <button type="button" data-testid={`${testid}-add-save`} onClick={save} disabled={saving} className="icon-btn p-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}</button>
        <button type="button" data-testid={`${testid}-add-cancel`} onClick={() => { setAdding(false); setDraft(""); }} className="icon-btn p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <Select value={value || NONE} onValueChange={(v) => { if (v === ADD) return setAdding(true); onChange(v === NONE ? "" : v); }}>
      <SelectTrigger data-testid={testid} className={className}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {!required && <SelectItem value={NONE}>—</SelectItem>}
        {value && !known && <SelectItem value={value}>{value}</SelectItem>}
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        <SelectItem value={ADD} data-testid={`${testid}-add-new`} className="text-teal-700 font-medium"><span className="inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Aggiungi nuovo valore…</span></SelectItem>
      </SelectContent>
    </Select>
  );
}
